import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard, SaveBar } from '@/pages/settings/ClinicGeneral';
import type { Json } from '@/types/database.generated';

interface BrandingShape {
  logo_url: string;
  primary_color: string;
}

const EMPTY_BRANDING: BrandingShape = { logo_url: '', primary_color: '' };

const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5 MB

function readBranding(value: Json): BrandingShape {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, Json>;
    return {
      logo_url: typeof v.logo_url === 'string' ? v.logo_url : '',
      primary_color: typeof v.primary_color === 'string' ? v.primary_color : '',
    };
  }
  return EMPTY_BRANDING;
}

export function ClinicBranding() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!clinic) return;
    const b = readBranding(clinic.branding);
    setLogoUrl(b.logo_url);
    setPrimaryColor(b.primary_color);
    
    // Load preview if logo exists in storage
    if (b.logo_url && b.logo_url.startsWith('clinic-logos/')) {
      loadLogoPreview(b.logo_url);
    } else if (b.logo_url) {
      setLogoPreview(b.logo_url);
    }
  }, [clinic]);

  const loadLogoPreview = async (storagePath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('clinic-logos')
        .createSignedUrl(storagePath.replace('clinic-logos/', ''), 3600);
      
      if (!error && data?.signedUrl) {
        setLogoPreview(data.signedUrl);
      }
    } catch (err) {
      console.error('Failed to load logo preview', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const handleLogoFile = (file: File | null) => {
    if (!file) {
      setLogoFile(null);
      setLogoPreview('');
      return;
    }

    // Validate file type
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error(t('settings.branding.invalid_logo_type'));
      return;
    }

    // Validate file size
    if (file.size > MAX_LOGO_SIZE) {
      toast.error(t('settings.branding.logo_too_large'));
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleLogoFile(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setLogoUrl('');
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!activeClinicId) return;

    try {
      let finalLogoUrl = logoUrl.trim();

      // Upload logo file if user selected one
      if (logoFile) {
        setUploadingLogo(true);
        
        // Determine file extension
        const ext = logoFile.name.split('.').pop() || 'png';
        const storagePath = `${activeClinicId}/logo.${ext}`;

        // Upload to storage (upsert to overwrite existing)
        const { error: uploadError } = await supabase.storage
          .from('clinic-logos')
          .upload(storagePath, logoFile, {
            upsert: true,
            contentType: logoFile.type,
          });

        if (uploadError) {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        finalLogoUrl = `clinic-logos/${storagePath}`;
        setUploadingLogo(false);
      }

      const branding: BrandingShape = {
        logo_url: finalLogoUrl,
        primary_color: primaryColor.trim(),
      };

      await update.mutateAsync({ branding: branding as unknown as Json });
      toast.success(t('settings.saved_toast'));
      
      // Clear file input after successful save
      setLogoFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('update_clinic_settings (branding) failed', err);
      toast.error(t('settings.save_error_toast'));
      setUploadingLogo(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <SectionCard
        title={t('settings.branding.title')}
        subtitle={t('settings.branding.subtitle')}
        readOnly={!canEdit}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('settings.branding.logo_upload')}
            </label>
            
            <div className="space-y-3">
              {/* File input */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_LOGO_TYPES.join(',')}
                  onChange={handleFileInputChange}
                  disabled={!canEdit}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-60 dark:file:bg-indigo-900/20 dark:file:text-indigo-400 dark:hover:file:bg-indigo-900/30"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.branding.logo_upload_hint')}
                </p>
              </div>

              {/* Preview */}
              {logoPreview && (
                <div className="relative inline-block">
                  <div className="h-24 w-24 border border-gray-300 rounded-xl bg-white dark:bg-gray-800 dark:border-gray-600 overflow-hidden flex items-center justify-center">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="branding-color"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('settings.branding.primary_color')}
            </label>
            <div className="mt-1 flex items-center gap-3">
              <input
                id="branding-color"
                type="color"
                value={primaryColor || '#4f46e5'}
                onChange={(e) => setPrimaryColor(e.target.value)}
                disabled={!canEdit}
                className="h-[44px] w-14 cursor-pointer rounded-xl border border-gray-300 bg-white disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                disabled={!canEdit}
                placeholder="#4f46e5"
                className="block h-[44px] flex-1 rounded-xl border border-gray-300 bg-white px-3 font-mono text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('settings.branding.primary_color_hint')}
            </p>
          </div>

        </div>
      </SectionCard>

      <SaveBar canEdit={canEdit} pending={update.isPending || uploadingLogo} />
    </form>
  );
}
